import React, { useCallback, useEffect, useState } from "react";
import { Inbox, X, Mail, MailOpen, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

// OwnerInbox — owner-only viewer of submissions posted to the ★ CONTACT
// virtual channel. Polls /api/contact/messages while open so a fresh
// transmission appears without a manual refresh, and exposes a tiny
// `useUnreadInbox` hook so OwnerBar can render a topbar badge.
//
// Why this lives outside OwnerBar: that file is already 1500+ lines and
// owns six other admin sub-panels. Keeping the inbox standalone makes the
// upcoming refactor easier and lets the panel manage its own fetch state.

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

async function fetchMessages(token) {
  const res = await fetch(`${API}/contact/messages`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Hook used by OwnerBar to drive the unread-count badge. Polls every 60s.
// Returns { unread, refresh } — `refresh()` lets the panel force a poke
// after marking/deleting so the badge stays in sync without waiting for
// the next interval.
export function useUnreadInbox(isOwner, getToken) {
  const [unread, setUnread] = useState(0);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!isOwner) {
      setUnread(0);
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const token = typeof getToken === "function" ? getToken() : "";
        const msgs = await fetchMessages(token);
        if (cancelled) return;
        const n = msgs.filter((m) => !m.read).length;
        setUnread(n);
      } catch (e) {
        /* noop — keep last value */
      }
    };
    poll();
    const t = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isOwner, getToken, tick]);

  return { unread, refresh };
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleString();
  } catch (e) { return iso; }
}

export default function OwnerInboxPanel({ isOwner, getToken, onClose, onChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionId, setActionId] = useState(null); // id of message currently being mutated

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      const msgs = await fetchMessages(token);
      setMessages(msgs);
    } catch (e) {
      toast.error("INBOX FETCH FAILED", { description: e.message || "Try again." });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!isOwner) return;
    load();
  }, [isOwner, load]);

  const markRead = async (id) => {
    setActionId(id);
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      const res = await fetch(`${API}/contact/messages/${id}`, {
        method: "PATCH",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMessages((list) => list.map((m) => (m.id === id ? { ...m, read: true } : m)));
      if (typeof onChange === "function") onChange();
    } catch (e) {
      toast.error("MARK READ FAILED", { description: e.message });
    } finally {
      setActionId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message? This can't be undone.")) return;
    setActionId(id);
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      const res = await fetch(`${API}/contact/messages/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMessages((list) => list.filter((m) => m.id !== id));
      if (expandedId === id) setExpandedId(null);
      if (typeof onChange === "function") onChange();
      toast.success("DELETED");
    } catch (e) {
      toast.error("DELETE FAILED", { description: e.message });
    } finally {
      setActionId(null);
    }
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("EMAIL COPIED", { description: email });
    } catch (e) {
      toast.error("COPY FAILED", { description: e.message });
    }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  if (!isOwner) return null;

  return (
    <div
      className="absolute top-12 right-0 w-[460px] max-h-[80vh] flex flex-col bg-black border-2 border-[var(--phosphor)] text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in_srgb,var(--phosphor)_35%,transparent)]"
      data-testid="inbox-panel"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
        <div className="text-xs tracking-[0.3em] flex items-center gap-2">
          <Inbox size={14} />
          ┌─ INBOX ─┐
          {unreadCount > 0 && (
            <span
              className="text-[9px] px-1.5 py-0.5 bg-[#FFCC00] text-black tracking-widest font-bold"
              data-testid="inbox-unread-pill"
            >
              {unreadCount} NEW
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-[var(--phosphor)]" data-testid="inbox-close" aria-label="close">
          <X size={14} />
        </button>
      </div>

      <div className="px-4 pb-2 text-[10px] opacity-70 leading-relaxed shrink-0">
        Messages submitted via the ★ CONTACT channel. Newest first.
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-crt px-2 pb-3" data-testid="inbox-list">
        {loading ? (
          <div className="text-center text-[11px] opacity-60 py-8">LOADING…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[11px] opacity-60 py-10" data-testid="inbox-empty">
            NO TRANSMISSIONS YET.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {messages.map((m) => {
              const expanded = expandedId === m.id;
              const busy = actionId === m.id;
              return (
                <li
                  key={m.id}
                  className={`border ${m.read ? "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]" : "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)]"}`}
                  data-testid={`inbox-row-${m.id}`}
                >
                  <button
                    onClick={() => {
                      const next = expanded ? null : m.id;
                      setExpandedId(next);
                      if (next && !m.read) markRead(m.id);
                    }}
                    className="w-full px-2.5 py-1.5 flex items-start gap-2 text-left"
                    data-testid={`inbox-row-toggle-${m.id}`}
                  >
                    {m.read
                      ? <MailOpen size={14} className="mt-0.5 opacity-60 shrink-0" />
                      : <Mail size={14} className="mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[12px] tracking-wide truncate ${m.read ? "opacity-70" : "font-bold"}`}>
                          {m.name || "(no name)"}
                        </span>
                        <span className="text-[9px] opacity-50 tabular-nums shrink-0 ml-auto">
                          {formatWhen(m.created_at)}
                        </span>
                      </div>
                      <div className="text-[10px] opacity-60 truncate">{m.email}</div>
                      {!expanded && (
                        <div className="text-[11px] opacity-75 truncate mt-0.5">{m.message}</div>
                      )}
                    </div>
                  </button>
                  {expanded && (
                    <div className="px-2.5 pb-2 pt-1 border-t border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]">
                      <pre
                        className="whitespace-pre-wrap break-words text-[11px] leading-relaxed opacity-90 font-mono mb-2 max-h-60 overflow-y-auto scrollbar-crt"
                        data-testid={`inbox-body-${m.id}`}
                      >{m.message}</pre>
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`mailto:${m.email}?subject=Re%3A%20your%20message%20to%20SCARTLEAD.TV`}
                          data-testid={`inbox-reply-${m.id}`}
                          className="flex-1 text-center py-1 text-[10px] tracking-widest border border-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors"
                        >
                          ▶ REPLY
                        </a>
                        <button
                          onClick={() => copyEmail(m.email)}
                          data-testid={`inbox-copy-${m.id}`}
                          title="Copy email"
                          className="py-1 px-2 text-[10px] tracking-widest border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:border-[var(--phosphor)] transition-colors"
                        >
                          <Copy size={11} />
                        </button>
                        <button
                          onClick={() => remove(m.id)}
                          disabled={busy}
                          data-testid={`inbox-delete-${m.id}`}
                          title="Delete"
                          className="py-1 px-2 text-[10px] tracking-widest border border-neutral-600 text-neutral-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors disabled:opacity-40"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] px-4 py-2 flex items-center justify-between text-[9px] opacity-60 shrink-0">
        <span>{messages.length} TOTAL · {unreadCount} UNREAD</span>
        <button
          onClick={load}
          data-testid="inbox-refresh"
          className="tracking-widest hover:text-[var(--phosphor)] hover:opacity-100"
        >
          ↻ REFRESH
        </button>
      </div>
    </div>
  );
}
